<template>
  <div class="container--pt container">
    <h1 class="title is-3">
      {{ $t("openRequest.header") }}
    </h1>

    <div class="columns is-multiline m-0 pb-5">
      <div class="details__content__box">
        <div class="column is-full">
          <h5 class="title is-5">{{ $t("findLocation") }} *</h5>
          <GeoLocationSelection @select-location="locationSelected" />
        </div>

        <div class="column is-full">
          <h5 class="title is-5">{{ $t("openRequest.textboxLabel") }} *</h5>
          <div class="control">
            <textarea
              class="textarea"
              v-model="description"
              id="description"
              name="description"
              rows="5"
              :placeholder="$t('openRequest.datesPlaceholder')"
            ></textarea>
          </div>
        </div>

        <div class="column is-full">
          <h5 class="title is-5">
            {{ $t("ReservationForm.billingDetails") }}
          </h5>
        </div>
        <div class="column is-half">
          <SelectedBusinessUnit
            :business-units="businessUnits"
            :selected-business-unit="selectedBusinessUnit"
            heading-tag="span"
            heading-class="u-weight--bold"
            required
            @selected-business-unit="selectBusinessUnit"
            @remove-business-unit="removeBusinessUnit"
          />

          <CustomField
            v-for="field in companyInfo.customFields"
            :key="field.name"
            :customField="field"
            @setted-value="setValue"
          ></CustomField>

          <button
            type="button"
            @click="submitReservation()"
            class="c-button c-button--primary is-medium mt-5"
            :disabled="isSaving || disableButton"
          >
            {{ treq("requestReservation") }}
          </button>

          <template v-if="isSaving">
            &nbsp;&nbsp;&nbsp;
            <img width="50" height="50" src="@/assets/images/loader.svg" />
          </template>
        </div>
      </div>
    </div>

    <NotificationMessage
      :level="notification.level"
      :title="notification.title"
      :message="notification.message"
      :show="notification.show"
      @toggle="toggleNotification"
    />
  </div>
</template>

<script>
import GeoLocationSelection from "@/components/reservationform/GeoLocationSelection";
import SelectedBusinessUnit from "@/components/reservationform/SelectedBusinessUnit.vue";
import CustomField from "@/components/reservationform/CustomField.vue";
import NotificationMessage from "@/components/shared/NotificationMessage.vue";
import CommonHelper from "@/helpers/common.helper";

export default {
  name: "openRequestReservation",
  components: {
    GeoLocationSelection,
    SelectedBusinessUnit,
    CustomField,
    NotificationMessage
  },
  async created() {
    this.businessUnits = await CommonHelper.getBusinessUnits();
    this.companyInfo = await CommonHelper.getCompanyInfo();
  },
  data() {
    return {
      selectedBusinessUnit: null,
      disableButton: false,
      businessUnits: [],
      companyInfo: {},
      customFieldFormData: [],
      notes: "",
      location: "",
      description: "",
      isSaving: false
    };
  },
  methods: {
    locationSelected(data) {
      this.location = data;
    },
    setValue(data) {
      this.customFieldFormData[data.field] = data.value;
    },
    selectBusinessUnit(data) {
      this.selectedBusinessUnit = data.businessUnit;
    },
    removeBusinessUnit() {
      this.selectedBusinessUnit = null;
    },
    validateForm() {
      this.errorMessages = [];
      const businessUnitMandatory = this.companyInfo.businessUnitMandatory;
      const businessUnitIsSelected = !!this.selectedBusinessUnit;
      let isValid = true;

      if (businessUnitMandatory && !businessUnitIsSelected) {
        isValid = false;
        let message = this.$t("error.businessunitMandatory");
        message = this.tbu(message);
        this.errorMessages.push(message);
      }

      if (!this.location) {
        isValid = false;
        this.errorMessages.push(this.$t("error.locationMandatory"));
      }

      if (!this.description || this.description.trim() === "") {
        isValid = false;
        this.errorMessages.push(this.$t("openRequest.datesMandatory"));
      }

      let customFieldsValid = true;
      let invalidCustomFields = [];
      (this.companyInfo.customFields || []).forEach((field) => {
        if (field.mandatory) {
          let thisFieldHasError = false;
          if (field.type === "checkbox") {
            if (
              !this.customFieldFormData[field.name] ||
              this.customFieldFormData[field.name].length < 1
            ) {
              thisFieldHasError = true;
            }
          } else {
            if (
              !this.customFieldFormData[field.name] ||
              this.customFieldFormData[field.name] === ""
            ) {
              thisFieldHasError = true;
            }
          }
          if (thisFieldHasError) {
            customFieldsValid = false;
            isValid = false;
            invalidCustomFields.push(this.getFieldNameTranslation(field));
          }
        }
      });

      if (!customFieldsValid) {
        let message = this.$t("error.customFields");
        message += ` (${invalidCustomFields.join(", ")})`;
        this.errorMessages.push(message);
      }

      if (!isValid) {
        const notificationMessage = `${this.$t(
          "error.genericIntro"
        )} <ul><li>- ${this.errorMessages.join("</li><li>- ")}</li></ul>`;
        this.notification.title = this.$t("error.genericTitle");
        this.notification.message = notificationMessage;
        this.notification.level = "danger";
        this.notification.show = true;
        setTimeout(() => {
          this.notification.show = false;
        }, 10 * 1000);
        return false;
      }
      return true;
    },
    async submitReservation() {
      if (!this.validateForm()) return;

      this.isSaving = true;
      this.disableButton = true;

      const postData = {
        description: this.description,
        location: this.location,
        businessUnit: this.selectedBusinessUnit?.id || null,
        customFields: {}
      };

      Object.keys(this.customFieldFormData).forEach((key) => {
        postData.customFields[key] = this.customFieldFormData[key];
      });

      const url = `${this.AVAILABILITY_API_URL}/reservation/openrequest`;
      const { status } = await this.axios.post(url, postData);

      if (status === 200) {
        this.isSaving = false;
        this.notification.title = this.$t("openRequest.savedTitle");
        this.notification.message = this.$t("openRequest.savedMessage");
        this.notification.show = true;
        this.notification.level = "info";
        setTimeout(() => {
          this.notification.show = false;
        }, 10 * 1000);
        setTimeout(() => {
          this.disableButton = false;
        }, 10 * 1000);
        this.$router.push("/openRequest-confirmation");
      } else {
        this.isSaving = false;
        this.disableButton = false;
        this.notification.title = this.$t("openRequest.errorTitle");
        this.notification.message = this.$t("openRequest.errorMessage");
        this.notification.show = true;
        this.notification.level = "danger";
        setTimeout(() => {
          this.notification.show = false;
        }, 10 * 1000);
      }
    }
  }
};
</script>
